import usePagination, { PaginationProps } from "~/components/analytics/shared/hooks/usePagination";
import ErrorState from "~/components/analytics/shared/ErrorState";
import EmptyState from "~/components/shared/EmptyState";
import Pagination from "~/components/shared/Pagination";
import { LoadingPanels } from "~/components/analytics/shared/LoadingStates";
import Tooltip from "~/components/analytics/shared/Tooltips/BasicTooltip";
import Row, { ExecutionRow } from "~/components/analytics/execution/Row";
import FeedbackMessage from "~/components/analytics/shared/FeedbackMessage";

type Props = {
  endpoint: string;
  tooltip: string;
};

const ExecutionsTable = ({ endpoint, tooltip }: Props) => {
  const { currentData, pageCount, handleOnChange, loading, error, metadata } = usePagination({
    endpoint,
  });

  return (
    <ExecutionsData
      currentData={currentData}
      pageCount={pageCount}
      handleOnChange={handleOnChange}
      loading={loading}
      error={error}
      tooltip={tooltip}
      metadata={metadata}
    />
  );
};

type ExecutionsDataPaginationProps = Omit<PaginationProps, "currentData"> & {
  currentData: Array<ExecutionRow>;
  tooltip: string;
};

export const ExecutionsData = ({
  currentData,
  loading,
  error,
  pageCount,
  handleOnChange,
  tooltip,
  metadata,
}: ExecutionsDataPaginationProps) => {
  const { total_results, period }: { total_results: number; period: string } = metadata;

  if (error) {
    return (
      <ErrorState
        emoji="🕳️"
        heading="There was an error loading the test executions"
        subheading="Try refreshing the page; if the problem persists"
        urlText="let us know"
        url="mailto:support@buildkite.com"
      />
    );
  }

  if (!loading && currentData.length === 0) {
    return <EmptyState emoji="🤖" heading="No test executions found" />;
  }

  return (
    <section id="test-executions">
      {loading ? (
        <LoadingPanels rows={3} columns={1} />
      ) : (
        <ul className="ta-panel">
          <li aria-hidden={true} className="ta-panel--heading grid-cols-[3rem_auto_6rem]">
            <div>Result</div>
            <div className="main-section">Description</div>
            <div className="flex items-center justify-end">
              Duration
              <Tooltip classNames="ml-1" name="Duration">
                <p className="charcoal-800 m-0 font-semibold">Duration</p>
                <p className="charcoal-800 m-0 mt-1">{tooltip}</p>
              </Tooltip>
            </div>
          </li>
          {currentData.map((execution) => (
            <Row key={execution.uuid} execution={execution} />
          ))}
        </ul>
      )}

      <Pagination pageCount={pageCount} onPageChange={handleOnChange} />

      {total_results && period && (
        <FeedbackMessage message={`${total_results} executions ran in the last ${period}.`} />
      )}
    </section>
  );
};

export default ExecutionsTable;
